import { FirebaseApp, getApp, getApps, initializeApp } from 'firebase/app';
import { initializeAuth, signInWithCustomToken } from 'firebase/auth';
import {
  Database,
  DataSnapshot,
  ref as dbRef,
  get,
  getDatabase,
  onChildAdded,
  onChildRemoved,
  onValue,
  push,
  remove,
  Unsubscribe,
  update,
} from 'firebase/database';
import {
  getDownloadURL,
  getStorage,
  ref as storageRef,
  uploadBytes,
} from 'firebase/storage';

import { GlobalAPIService } from '@core';
import { DataSourceConfig } from '@stores';

import { DataBaseNodes } from './firebase.types';

export class FirebaseService {
  private readonly options: DataSourceConfig;
  private app: FirebaseApp;
  private readonly db: Database;

  constructor(options: DataSourceConfig) {
    this.options = options;
    this.app = this.initialize();
    this.db = getDatabase(this.app);
  }

  getApp = () => {
    return this.app;
  };

  getDb = () => {
    return this.db;
  };

  getDbRef = (query?: string) => {
    return dbRef(this.db, query);
  };

  initialize = () => {
    const app = getApps().find(app => app.name === this.options.projectId);

    return (
      app ||
      initializeApp(
        {
          ...this.options,
        },
        this.options.projectId
      )
    );
  };

  authenticate = async () => {
    this.app = getApp(this.options.projectId);
    const auth = initializeAuth(this.app);

    const customTokenResult = await GlobalAPIService.getCustomToken(
      this.options.patientSourceUUID,
      this.options.databaseURL!
    );

    if (!customTokenResult) return false;

    await signInWithCustomToken(auth, customTokenResult.customToken).catch(
      error => {
        throw new Error(
          `Something went wrong on signInWithCustomToken: ${error}`
        );
      }
    );
    // yield firebaseApp.removeCareplansAndRemindersNodesOnSSOLogin()
    // yield firebaseApp.registerChildAddedListenerForDummyLoadingPhases(firebasePatientId)
    return this.app;
  };

  readDataOnce = async <T>(query: string) => {
    const queryRef = this.getDbRef(query);

    const snapshot = await get(queryRef).catch(error => {
      throw new Error(
        `Can not read data from Firebase. Error: ${JSON.stringify(error)}`
      );
    });
    return snapshot.exists() ? (snapshot.val() as T) : null;
  };

  removeData = async (query: string) => {
    const removeRef = this.getDbRef(query);

    return await remove(removeRef);
  };

  getMaintenanceMode = async () => {
    const isMaintenanceMode = await this.readDataOnce<boolean>(
      DataBaseNodes.MaintenanceMode
    );

    return isMaintenanceMode === true;
  };

  getServerVersion = async () => {
    const serverVersion = await this.readDataOnce<string>(
      DataBaseNodes.ServerVersion
    );

    return serverVersion;
  };

  writeData = <T>(query: string, payload: T, key?: string) => {
    const writeRef = this.getDbRef(query);
    const value = key ? { [key]: payload } : { ...payload };
    return push(writeRef, value);
  };

  updateData = <T>(query: string, payload: T) => {
    const dbRef = this.getDbRef();
    return update(dbRef, {
      [query]: payload,
    });
  };

  subscribeToChildAdded = (
    query: string,
    callback: (
      snapshot: DataSnapshot,
      previousChildName?: string | null
    ) => unknown
  ): Unsubscribe => {
    const childRef = this.getDbRef(query);
    return onChildAdded(childRef, callback);
  };

  subscribeToChildRemoved = (
    query: string,
    callback: (snapshot: DataSnapshot) => unknown
  ): Unsubscribe => {
    const childRef = this.getDbRef(query);
    return onChildRemoved(childRef, callback);
  };

  subscribeToChild = (
    query: string,
    callback: (snapshot: DataSnapshot) => unknown
  ): Unsubscribe => {
    const childRef = this.getDbRef(query);
    return onValue(childRef, callback);
  };

  uploadFile = (file: File, baseQuery: string) => {
    const storage = getStorage(this.app);
    const fileRef = storageRef(storage, baseQuery + file.name);

    return uploadBytes(fileRef, file).then(async response => {
      return {
        fileName: response.metadata.name,
        size: response.metadata.size,
        directLink: await getDownloadURL(fileRef),
      };
    });
  };
}
